import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { clamp } from '@/lib/depth';
import { cn } from '@/lib/utils';

/**
 * WordsPullUp — each word slides up from y:20 with a staggered 0.08s delay.
 * Driven by the `active` prop (set when the card enters camera range in 3D,
 * or by useInView in the mobile fallback).
 */
export const WordsPullUp = ({ text, active = true, className, delayStart = 0 }) => {
  const words = text.split(' ');
  return (
    <span className={cn('inline', className)}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: 24, opacity: 0 }}
            animate={active ? { y: 0, opacity: 1 } : { y: 24, opacity: 0 }}
            transition={{ delay: delayStart + i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}
            {i < words.length - 1 ? '\u00A0' : ''}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

/**
 * AnimatedLetters — scroll-linked character opacity from 0.2 to 1.
 * In the 3D experience it reads `data-depth-progress` written each frame by
 * the depth engine on the section host. On mobile it derives progress from
 * the element's viewport position.
 */
export const AnimatedLetters = ({ text, className }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const spans = Array.from(el.querySelectorAll('[data-ch="1"]'));
    const host = el.closest('[data-depth-host="1"]');
    const n = spans.length || 1;
    let raf;
    const tick = () => {
      let p;
      if (host) {
        p = parseFloat(host.dataset.depthProgress || '0');
      } else {
        const r = el.getBoundingClientRect();
        p = clamp(1.15 - r.top / (window.innerHeight * 0.75), 0, 1);
      }
      for (let i = 0; i < n; i += 1) {
        const cp = clamp(p * 1.45 - (i / n) * 0.9, 0, 1);
        spans[i].style.opacity = (0.2 + 0.8 * cp).toFixed(2);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);

  const words = text.split(' ');
  return (
    <p ref={ref} className={className}>
      {words.map((word, wi) => (
        <span key={wi} className="inline-block whitespace-pre">
          {word.split('').map((ch, ci) => (
            <span key={ci} data-ch="1" style={{ opacity: 0.2 }}>
              {ch}
            </span>
          ))}
          {wi < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </p>
  );
};

/** Small uppercase section label */
export const SectionLabel = ({ children, ember = false, className }) => (
  <p
    className={cn(
      'text-[10px] font-bold uppercase tracking-[0.32em] mb-3',
      ember ? 'text-ember-soft' : 'text-primary',
      className
    )}
  >
    {children}
  </p>
);
